const { Terminal } = require("@xterm/xterm");
const { FitAddon } = require("@xterm/addon-fit");
const { WebLinksAddon } = require("@xterm/addon-web-links");
const { SearchAddon } = require("@xterm/addon-search");
const { UnicodeGraphemesAddon } = require("@xterm/addon-unicode-graphemes");
const { SerializeAddon } = require("@xterm/addon-serialize");
const { ClipboardAddon } = require("@xterm/addon-clipboard");
const { WebglAddon } = require("@xterm/addon-webgl");
const { sanitizeTryitCommand, isTrustedPrefillMessage } = require("./tryit-prefill");
const terminalComponent = document.getElementById("terminal");
const statusIndicator = document.querySelector('.status-indicator');
const connectionStatus = document.getElementById('connection-status');
const rendererType = document.getElementById('renderer-type');
const commandCount = document.getElementById('command-count');
const terminalSize = document.getElementById('terminal-size');

const TERMINAL_CONFIG = {
  DIMENSIONS: { DEFAULT_COLS: 100, DEFAULT_ROWS: 24, MIN_COLS: 40, MIN_ROWS: 10 },
  FONT: { DEFAULT_SIZE: 14, MIN_SIZE: 10, MAX_SIZE: 24, FAMILY: 'monospace, courier-new, courier, monospace' },
  SCROLLBACK: 5000
};

const DEFAULT_THEME = {
  background: '#000000', foreground: '#FFFFFF', cursor: '#FFFFFF', cursorAccent: '#000000',
  selection: 'rgba(255, 255, 255, 0.3)',
  black: '#000000', red: '#C91B00', green: '#00C200', yellow: '#C7C400',
  blue: '#0225C7', magenta: '#CA30C7', cyan: '#00C5C7', white: '#C7C7C7',
  brightBlack: '#686868', brightRed: '#FF6E67', brightGreen: '#5FF967', brightYellow: '#FEFB67',
  brightBlue: '#6871FF', brightMagenta: '#FF76FF', brightCyan: '#5FFDFF', brightWhite: '#FFFFFF'
};

const TERMINAL_OPTIONS = {
  cursorBlink: true,
  cursorStyle: 'block',
  fontSize: TERMINAL_CONFIG.FONT.DEFAULT_SIZE,
  fontFamily: TERMINAL_CONFIG.FONT.FAMILY,
  theme: DEFAULT_THEME,
  allowTransparency: true,
  scrollback: TERMINAL_CONFIG.SCROLLBACK,
  cols: TERMINAL_CONFIG.DIMENSIONS.DEFAULT_COLS,
  rows: TERMINAL_CONFIG.DIMENSIONS.DEFAULT_ROWS,
  allowProposedApi: true,
  convertEol: true,
  rightClickSelectsWord: true,
  drawBoldTextInBrightColors: true,
  bracketedPaste: true,
  macOptionIsMeta: true,
  macOptionClickForcesSelection: false,
  altClickMovesCursor: false,
  screenReaderMode: false,
  disableStdin: false
};

const CONNECTION_CONFIG = {
  RECONNECT_ATTEMPTS: 5,
  RECONNECT_INTERVAL_MS: 2000,
  INITIAL_COMMAND_DELAY_MS: 500
};

const STATUS_MESSAGES = {
  CONNECTING: {
    INITIAL: '\x1b[36m[*] Initializing terminal connection...\x1b[0m',
    ATTEMPT: '\x1b[36m[*] Attempting to establish secure connection to pod...\x1b[0m'
  },
  CONNECTED: {
    SUCCESS: '\x1b[32m[✓] Connection established\x1b[0m',
    RECONNECTED: '\x1b[32m[✓] Connection successfully re-established!\x1b[0m',
    READY: '\x1b[32m[✓] Terminal ready for input\x1b[0m'
  },
  DISCONNECTED: {
    LOST: '\x1b[31m[*] Connection lost. Attempting to restore connection...\x1b[0m',
    ATTEMPT: '\x1b[31m[*] Reconnection attempt {current}/{max}\x1b[0m',
    MAX_ATTEMPTS: '\x1b[31m[!] Maximum reconnection attempts reached.\x1b[0m',
    REFRESH: '\x1b[31m[!] Please refresh the page to try again.\x1b[0m'
  },
  ERROR: {
    CONNECTION: '\x1b[31m[!] Failed to connect: {message}\x1b[0m',
    ENV_VARS: '\x1b[31m[!] Failed to get instance ID: {message}\x1b[0m'
  }
};

let term = null;
let fitAddon = null;
let webglAddon = null;
let commandCounter = 0;
let reconnectAttempts = 0;
let reconnectTimeout = null;
let currentInstanceId = null;
let currentFontSize = TERMINAL_CONFIG.FONT.DEFAULT_SIZE;
let sessionId = null;
let eventSource = null;
let lastCtrlCTime = 0;
let visibilityPingInterval = null;

const updateConnectionStatusDisplay = (message, type = 'info') => {
  if (connectionStatus) connectionStatus.textContent = message;
  if (statusIndicator) {
    statusIndicator.className = 'status-indicator';
    if (type === 'success') statusIndicator.classList.add('connected');
    else if (type === 'error') statusIndicator.classList.add('disconnected');
  }
  const statusBar = document.querySelector('.status-bar');
  if (statusBar) statusBar.className = `status-bar status-${type}`;
};

const updateTerminalSize = () => {
  if (term && terminalSize) {
    const { rows, cols } = term;
    terminalSize.textContent = `Size: ${cols}x${rows}`;
  }
};

const updateCommandCount = () => {
  if (commandCount) commandCount.textContent = `Commands: ${commandCounter}`;
};

const updateFontSize = () => {
  const el = document.getElementById('font-size');
  if (term && el) el.textContent = `${currentFontSize}px`;
};

const termFetch = async (url, options = {}) => {
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
  });
  // A 401 mid-session means JWT expired; surface it so keystrokes don't silently drop.
  if (res.status === 401) {
    updateConnectionStatusDisplay('Session expired — reload to reconnect', 'error');
  }
  return res;
};

const initializeTerminal = () => {
  term = new Terminal(TERMINAL_OPTIONS);

  fitAddon = new FitAddon();
  const webLinksAddon = new WebLinksAddon();
  const searchAddon = new SearchAddon();
  const unicodeGraphemesAddon = new UnicodeGraphemesAddon();
  const serializeAddon = new SerializeAddon();

  term.loadAddon(fitAddon);
  term.loadAddon(webLinksAddon);
  term.loadAddon(searchAddon);
  term.loadAddon(unicodeGraphemesAddon);
  term.loadAddon(serializeAddon);
  // e2e hook: webgl renderer leaves no DOM text to read
  window.__e2eTerminalText = () => serializeAddon.serialize();
  term.loadAddon(new ClipboardAddon());

  term.open(terminalComponent);
  fitAddon.fit();

  try {
    webglAddon = new WebglAddon();
    term.loadAddon(webglAddon);
    webglAddon.onContextLoss(() => {
      webglAddon.dispose();
      if (rendererType) rendererType.textContent = 'Renderer: DOM';
    });
    if (rendererType) rendererType.textContent = 'Renderer: WebGL';
  } catch (e) {
    if (rendererType) rendererType.textContent = 'Renderer: DOM';
  }
  updateTerminalSize();
  updateFontSize();

  // Batch key input to reduce HTTP requests (important for vim)
  let inputBuffer = "";
  let inputTimeout = null;
  const INPUT_BATCH_DELAY = 5;

  const sendBufferedInput = () => {
    if (inputBuffer && sessionId) {
      termFetch(`/terminal/input/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: inputBuffer })
      }).catch(error => console.error('Error sending buffered input:', error));
      inputBuffer = "";
    }
  };

  const IMMEDIATE_SEND_KEYS = [
    '\x03', '\x1b', '\x1b[A', '\x1b[B', '\x1b[C', '\x1b[D',
    '\x1b[H', '\x1b[F', '\r', '\n', '\x7f', '\x08', '\t'
  ];

  term.attachCustomKeyEventHandler((event) => {
    if (event.type !== 'keydown') return true;

    if (event.ctrlKey && event.shiftKey && event.code === 'KeyC') {
      const selection = term.getSelection();
      if (selection) navigator.clipboard.writeText(selection).catch(() => {});
      event.preventDefault();
      return false;
    }
    if (event.ctrlKey && event.shiftKey && event.code === 'KeyF') {
      searchAddon.show();
      event.preventDefault();
      return false;
    }
    if ((event.ctrlKey || event.metaKey) && event.code === 'KeyC' && !term.hasSelection()) {
      event.preventDefault();
      const now = Date.now();
      if (now - lastCtrlCTime < 300) return false;
      lastCtrlCTime = now;
      if (sessionId) {
        termFetch(`/terminal/input/${sessionId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: '\x03' })
        }).catch(error => console.error('Error sending interrupt:', error));
      }
      return false;
    }
    if (event.ctrlKey && event.code === 'KeyU') {
      event.preventDefault();
      if (sessionId) {
        termFetch(`/terminal/input/${sessionId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: '\x15' })
        }).catch(error => console.error('Error sending clear line:', error));
      }
      return false;
    }
    if (event.key === 'Tab') {
      if (sessionId) {
        termFetch(`/terminal/input/${sessionId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: '\t' })
        }).catch(error => console.error('Error sending tab:', error));
      }
      event.preventDefault();
      return false;
    }
    return true;
  });

  term.onData((data) => {
    if (!sessionId) return;

    if (IMMEDIATE_SEND_KEYS.includes(data)) {
      if (inputBuffer) sendBufferedInput();
      termFetch(`/terminal/input/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data })
      }).catch(error => console.error('Error sending immediate key:', error));
      if (data === '\r' || data === '\n') {
        commandCounter++;
        updateCommandCount();
      }
      return;
    }

    inputBuffer += data;
    if (inputTimeout) clearTimeout(inputTimeout);
    inputTimeout = setTimeout(() => {
      sendBufferedInput();
      inputTimeout = null;
    }, INPUT_BATCH_DELAY);
  });

  const fontIncrease = document.getElementById('font-increase');
  const fontDecrease = document.getElementById('font-decrease');
  if (fontIncrease) {
    fontIncrease.addEventListener('click', () => {
      currentFontSize = Math.min(currentFontSize + 1, TERMINAL_CONFIG.FONT.MAX_SIZE);
      term.options.fontSize = currentFontSize;
      fitAddon.fit();
      updateFontSize();
    });
  }
  if (fontDecrease) {
    fontDecrease.addEventListener('click', () => {
      currentFontSize = Math.max(currentFontSize - 1, TERMINAL_CONFIG.FONT.MIN_SIZE);
      term.options.fontSize = currentFontSize;
      fitAddon.fit();
      updateFontSize();
    });
  }

  return term;
};

const attachSSEStream = (targetSessionId, { reconnect = false, replay = null } = {}) => {
  if (eventSource) { eventSource.close(); eventSource = null; }

  // reconnect: skip replay, living xterm holds scrollback. replay='full': whole buffer into
  // a fresh xterm (reattach after reload). default: server replays the last 5s.
  const q = reconnect ? '?reconnect=1' : (replay === 'full' ? '?replay=full' : '');
  const url = `/terminal/output/${targetSessionId}${q}`;
  eventSource = new EventSource(url, { withCredentials: true });

  eventSource.onmessage = (event) => {
    const data = JSON.parse(event.data);
    if (data.data) term.write(data.data);
    if (data.exit) {
      // PTY process died — clean up and create a fresh session
      if (eventSource) { eventSource.close(); eventSource = null; }
      const deadSessionId = sessionId;
      sessionId = null;
      closeOldSession(deadSessionId);
      reconnectTimeout = setTimeout(() => createNewSession(currentInstanceId), CONNECTION_CONFIG.RECONNECT_INTERVAL_MS);
    }
  };

  eventSource.onopen = () => {
    const wasReconnect = reconnectAttempts > 0;
    reconnectAttempts = 0;
    updateConnectionStatusDisplay('Connected', 'success');

    if (document.visibilityState !== 'visible') {
      if (visibilityPingInterval) clearInterval(visibilityPingInterval);
      visibilityPingInterval = setInterval(ping, 5000);
    }

    if (wasReconnect) {
      term.writeln(STATUS_MESSAGES.CONNECTED.RECONNECTED);
    }
  };

  eventSource.onerror = () => {
    if (eventSource && eventSource.readyState === 2) {
      eventSource.close();
      eventSource = null;
      if (term) term.writeln('\r\n' + STATUS_MESSAGES.DISCONNECTED.LOST);
      updateConnectionStatusDisplay('Disconnected', 'error');
      if (document.visibilityState === 'visible') {
        reconnectTimeout = setTimeout(handleReconnectionAttempt, CONNECTION_CONFIG.RECONNECT_INTERVAL_MS);
      }
    }
  };
};

const checkSessionAlive = async (targetSessionId) => {
  try {
    const response = await termFetch(`/terminal/status/${targetSessionId}`);
    if (!response.ok) return false;
    const data = await response.json();
    return data.processAlive === true;
  } catch {
    return false;
  }
};

const closeOldSession = (oldSessionId) => {
  if (!oldSessionId) return;
  termFetch(`/terminal/close/${oldSessionId}`, { method: 'POST' }).catch(() => {});
};

const handleVisibilityChange = () => {
  if (visibilityPingInterval) {
    clearInterval(visibilityPingInterval);
    visibilityPingInterval = null;
  }

  if (document.visibilityState === 'visible') {
    if (sessionId && (!eventSource || eventSource.readyState === 2)) {
      reconnectToSession();
    } else if (sessionId && eventSource) {
      ping();
    }
  } else {
    if (sessionId) {
      visibilityPingInterval = setInterval(() => {
        if (sessionId) ping();
        else { clearInterval(visibilityPingInterval); visibilityPingInterval = null; }
      }, 5000);
    }
  }
};

const ping = () => {
  if (!sessionId) return;
  termFetch(`/terminal/heartbeat/${sessionId}`)
    .then(response => {
      if (!response.ok) {
        if (eventSource) { eventSource.close(); eventSource = null; }
        if (document.visibilityState === 'visible') {
          reconnectToSession();
        }
      }
    })
    .catch(() => {});
};

const reconnectToSession = async () => {
  if (reconnectTimeout) { clearTimeout(reconnectTimeout); reconnectTimeout = null; }

  if (sessionId) {
    const alive = await checkSessionAlive(sessionId);
    if (alive) {
      updateConnectionStatusDisplay('Reconnecting...', 'info');
      attachSSEStream(sessionId, { reconnect: true });
      handleResize();
      return;
    }
  }

  const oldSessionId = sessionId;
  sessionId = null;
  closeOldSession(oldSessionId);
  await createNewSession(currentInstanceId);
};

const handleReconnectionAttempt = () => {
  reconnectAttempts++;
  if (reconnectAttempts <= CONNECTION_CONFIG.RECONNECT_ATTEMPTS) {
    if (term) {
      term.writeln(
        STATUS_MESSAGES.DISCONNECTED.ATTEMPT
          .replace('{current}', reconnectAttempts)
          .replace('{max}', CONNECTION_CONFIG.RECONNECT_ATTEMPTS)
      );
    }
    reconnectToSession();
  } else {
    if (term) {
      term.writeln(STATUS_MESSAGES.DISCONNECTED.MAX_ATTEMPTS);
      term.writeln(STATUS_MESSAGES.DISCONNECTED.REFRESH);
    }
    updateConnectionStatusDisplay('Connection lost', 'error');
  }
};

// Notify parent WebOS window of the active sessionId so it can POST /terminal/close on
// explicit window close. Sent for both freshly-created and reattached sessions.
const notifyParentSessionCreated = (sid) => {
  if (window.parent !== window && window.__TERMINAL_CONFIG__?.webosOrigin) {
    window.parent.postMessage({ type: 'terminal-session-created', sessionId: sid }, window.__TERMINAL_CONFIG__.webosOrigin);
  }
};

const createNewSession = async (instanceId) => {
  try {
    const createResponse = await termFetch('/terminal/create', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        instanceId,
        cols: term.cols,
        rows: term.rows
      })
    });

    if (!createResponse.ok) {
      const errorData = await createResponse.json();
      throw new Error(errorData.error || `HTTP error! status: ${createResponse.status}`);
    }

    const sessionData = await createResponse.json();
    sessionId = sessionData.sessionId;

    notifyParentSessionCreated(sessionId);
    attachSSEStream(sessionId);
    handleResize();
  } catch (error) {
    console.error('Error creating terminal session:', error);
    if (term) {
      term.writeln("");
      term.writeln(STATUS_MESSAGES.ERROR.CONNECTION.replace('{message}', error.message));
    }
    updateConnectionStatusDisplay('Connection failed', 'error');
    reconnectTimeout = setTimeout(handleReconnectionAttempt, CONNECTION_CONFIG.RECONNECT_INTERVAL_MS);
  }
};

const connectToTerminalSession = async (instanceId) => {
  currentInstanceId = instanceId;

  if (eventSource) { eventSource.close(); eventSource = null; }
  if (visibilityPingInterval) { clearInterval(visibilityPingInterval); visibilityPingInterval = null; }
  if (!term) term = initializeTerminal();
  if (reconnectTimeout) { clearTimeout(reconnectTimeout); reconnectTimeout = null; }

  if (reconnectAttempts === 0) term.clear();

  // Restored terminal windows carry ?session=; reattach to the live PTY (full buffer into a
  // fresh xterm) when it is still alive, otherwise fall through to a fresh session transparently.
  const persisted = getSessionParam();
  if (persisted && await checkSessionAlive(persisted)) {
    sessionId = persisted;
    notifyParentSessionCreated(sessionId);
    updateConnectionStatusDisplay('Reconnecting to session...', 'info');
    attachSSEStream(sessionId, { replay: 'full' });
    handleResize();
    return;
  }

  updateConnectionStatusDisplay('Connecting...', 'info');
  await createNewSession(instanceId);
};

let resizeDebounceTimer = null;
const handleResize = () => {
  if (resizeDebounceTimer) clearTimeout(resizeDebounceTimer);
  resizeDebounceTimer = setTimeout(() => {
    resizeDebounceTimer = null;
    if (fitAddon && term) {
      fitAddon.fit();
      updateTerminalSize();
      if (sessionId) {
        termFetch(`/terminal/resize/${sessionId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cols: term.cols, rows: term.rows })
        }).catch(error => console.error('Error resizing terminal:', error));
      }
    }
  }, 100);
};

const getInstanceId = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('instance');
};

const getSessionParam = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('session');
};

const initializeApplication = async () => {
  try {
    const instanceId = getInstanceId();

    if (!instanceId) {
      if (!term) term = initializeTerminal();
      term.writeln('\x1b[31m[!] No instance ID provided. Add ?instance=<id> to the URL.\x1b[0m');
      updateConnectionStatusDisplay('No instance ID', 'error');
      return;
    }

    await connectToTerminalSession(instanceId);

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  } catch (error) {
    console.error('Error initializing application:', error);
    if (term) {
      term.writeln(`\r\n\x1b[31m[!] Initialization error: ${error.message}\x1b[0m`);
    }
  }
};

// Page hide/unload closes the SSE socket for hygiene but does NOT reap the PTY: reattach after a
// reload or laptop-close depends on the server-side session surviving unload. The PTY is reaped
// server-side on genuine abandon (detached 15-min reattach grace / 1h max-idle) or on instance stop
// (term.onExit). Explicit terminal-window close still reaps via the WebOS parent's POST /terminal/close.
const closeStreamOnHide = () => {
  if (eventSource) { eventSource.close(); eventSource = null; }
  if (visibilityPingInterval) { clearInterval(visibilityPingInterval); visibilityPingInterval = null; }
};

// `:::tryit` prefill: WebOS relays a command; land it at the prompt WITHOUT a
// trailing CR so the student presses Enter (never auto-executed). Origin-pinned
// to the WebOS parent, mirroring the outbound terminal-session-created check.
const handleTryitPrefill = (event) => {
  const webosOrigin = window.__TERMINAL_CONFIG__?.webosOrigin;
  if (!isTrustedPrefillMessage(event, webosOrigin)) return;
  if (!sessionId) return; // no active PTY to prefill
  const command = sanitizeTryitCommand(event.data.command);
  if (!command) return;
  termFetch(`/terminal/input/${sessionId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: command })
  }).catch(error => console.error('Error prefilling tryit command:', error));
};

window.addEventListener('message', handleTryitPrefill);
window.addEventListener('load', initializeApplication);
window.addEventListener('pagehide', closeStreamOnHide);
// bfcache restore: JS state is intact but the SSE socket was closed on hide — reattach to the
// still-live PTY (checkSessionAlive gates it; falls through to a fresh session if it died).
window.addEventListener('pageshow', (event) => {
  if (event.persisted && sessionId) reconnectToSession();
});
